/*
 * File: permission-check.ts
 * File Created: 2023-11-27 03:21:15
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-02-05 20:12:04
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { ArrayHelper } from "./array-helper";

export interface IPermissionCheckURI {
    uris: string | string[];
    method?: "Post" | "Patch" | "Put" | "Delete";
    $permissions?: any;
}
export interface IPermissionCheckRole {
    roles: string | string[];
    $user?: any;
}
/// row: the data row to be edited / deleted
export type IPermissionCheckCustom = { (this: Vue, row?: any): boolean };

export type IPermissionCheck =
    | IPermissionCheckURI
    | IPermissionCheckRole
    | IPermissionCheckCustom
    /// fast way for true & false
    | boolean;

export interface IRouterAuth {
    /// allow & deny has the higher priority than permission
    /// allow the role(s)
    allow?: IPermissionCheck;
    /// deny the role(s)
    deny?: IPermissionCheck;
}

export class PermissionHelper {
    /// auth for router permission
    static authRouter(this: Vue, router: IRouterAuth): boolean {
        const version = (this as any).$permissionsVersion ?? 0;
        void version;
        do {
            /// 1) check deny / allow
            /// 1.1) deny
            let deny = router.deny;
            if (deny != undefined) {
                /// if deny, break to false
                if (PermissionHelper.check.call(this, deny)) break;
            }
            /// 1.2) allow
            let allow = router.allow;
            if (allow != undefined) {
                /// if !allow, break to false
                if (!PermissionHelper.check.call(this, allow)) break;
            }

            /// 2) eventually, pass the auth
            return true;
        } while (0);

        /// break to false
        return false;
    }

    static check(this: Vue, config: IPermissionCheck, data?: any): boolean {
        const version = (this as any).$permissionsVersion ?? 0;
        void version;
        return config === true
            ? true
            : config === false
              ? false
              : typeof config === "function"
                ? PermissionHelper.checkCustom.call(this, config, data)
                : "uris" in config
                  ? PermissionHelper.checkUri.call(this, config)
                  : "roles" in config
                    ? PermissionHelper.checkRole.call(this, config)
                    : false;
    }

    static checkUri(this: Vue, config: IPermissionCheckURI): boolean {
        const version = (this as any).$permissionsVersion ?? 0;
        void version;
        let { uris, method, $permissions } = config;
        /// default to "Patch"
        method = method ?? "Patch";
        uris = Array.isArray(uris) ? uris : [uris];
        $permissions = $permissions ?? this?.$permissions;

        /// /(:key)? => (\/:key)?
        const regex1 = /\/\(([^)]+)\)\?$/g;
        /// :key => [a-z0-9_-]+
        const regex2 = /(\:[a-z][a-z0-9_-]*)/g;

        main: for (let uri of uris) {
            /// 1) match whole word
            let perm = ($permissions[uri] || {})[method];
            if (perm) continue;

            /// 2) match rule
            let permKeys = Object.keys($permissions);
            for (let permKey of permKeys) {
                let key = permKey;
                let test1 = regex1.test(key);
                if (test1) key = key.replace(regex1, (a, b) => `(/${b})?`);
                let test2 = regex2.test(key);
                if (test2) key = key.replace(regex2, (a, b) => "[a-z0-9_-]+");
                if (test1 || test2) {
                    let match = new RegExp(key).test(uri);
                    if (match) {
                        let perm = ($permissions[permKey] || {})[method];
                        if (perm) continue main;
                    }
                }
            }
            /// fallover, not pass
            return false;
        }
        return true;
    }

    static checkRole(this: Vue, config: IPermissionCheckRole): boolean {
        const version = (this as any).$permissionsVersion ?? 0;
        void version;
        let { roles, $user } = config;
        const targetRoles = Array.isArray(roles) ? roles : [roles];
        $user = $user ?? this?.$user;
        const userRoles = $user?.roles ?? [];

        const { remain } = ArrayHelper.findDiff(targetRoles, userRoles, (a, b) => a == b);
        if (remain == null) return false;
        return remain.length === targetRoles.length;
    }

    static checkCustom(this: Vue, config: IPermissionCheckCustom, data?: any): boolean {
        const version = (this as any).$permissionsVersion ?? 0;
        void version;
        return config.call(this, data);
    }
}
